/**
 * Illustrative data for surfaces whose backend does not exist yet.
 *
 * Drawn from the design mockup (ENG-1520 / ENG-1523 / SUP-474 patterns). Only
 * used when the "Preview with sample data" switch is on; every card rendered
 * from it shows a "Sample" chip. Real WireGuard tunnels and real sites are
 * never replaced by this — it only fills the gaps listed in
 * docs/troubleshooting-api-gaps.md.
 *
 * Each builder takes the named scenario picked next to the switch
 * (sampleScenarios.ts). `mixed`, the default, is the original mockup; the
 * others bend every surface toward one situation. A few builders
 * (SAMPLE_TUNNELS, SAMPLE_SITE_DETAIL, SAMPLE_CAMERA_LIVENESS,
 * SAMPLE_SAVED_LOGIN_STATUS) overlay real data or fill a surface only for a
 * named scenario, so `mixed` still leaves those real.
 */
import {
  AlarmReceiver,
  AlertsReceived,
  CameraLastAlarms,
  CameraLiveness,
  RefreshStatus,
  SiteConnectionDetail,
  SettingsCompare,
  SiteChanges,
  CredentialCheckResponse,
  PortCheckRequest,
  PortCheckResponse,
  ReceiverTestRecord,
  SiteToSiteCheckResult,
  SiteToSiteConnection,
  SmtpFilenamesResponse,
  SmtpRecentResponse,
  WireGuardPeerTelemetry,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";
import { SampleScenarioKey } from "./sampleScenarios";

/** Scenarios where nothing reaches Actuate from the site. */
const SILENT_SCENARIOS: SampleScenarioKey[] = [
  "disarmed",
  "inactive",
  "connector-silent",
  "no-video",
  "wg-stale",
  "s2s-down",
  "smtp-mismatch",
  "milestone-login-refused",
];
const isSilent = (scenario: SampleScenarioKey) =>
  SILENT_SCENARIOS.includes(scenario);

const minutesAgo = (minutes: number) =>
  new Date(Date.now() - minutes * 60 * 1000).toISOString();

const withAwsTunnelsUp = (
  connection: SiteToSiteConnection
): SiteToSiteConnection =>
  connection.kind !== "aws_site_to_site"
    ? connection
    : {
        ...connection,
        status: { status: "up", tunnels_up: 2, tunnels_total: 2 },
        tunnels: connection.tunnels.map((tunnel) => ({
          ...tunnel,
          state: "UP" as const,
          status_message: "",
        })),
      };

export const SAMPLE_SITE_TO_SITE = (
  siteIds: number[],
  scenario: SampleScenarioKey = "mixed"
): SiteToSiteConnection[] => {
  const [a = 1, b = 2, c = 3, d = 4] = siteIds;
  const all: SiteToSiteConnection[] = [
    {
      id: "osnet-1",
      name: "OSNET-1, Management Server Schools Main",
      customer_gateway_ip: "69.24.198.150",
      actuate_gateway: "AWS VGW us-east-1",
      kind: "aws_site_to_site",
      status: { status: "down", tunnels_up: 0, tunnels_total: 2 },
      probe_target: "172.16.254.10",
      probe_port: 80,
      probe_label: "Management server",
      tunnels: [
        {
          outside_ip: "3.209.14.20",
          state: "DOWN",
          status_message: "IPSEC IS DOWN",
          last_status_change: minutesAgo(60 * 26),
        },
        {
          outside_ip: "52.4.88.101",
          state: "DOWN",
          status_message: "IPSEC IS DOWN",
          last_status_change: minutesAgo(60 * 26),
        },
      ],
      routes: ["172.16.254.0/24"],
      site_ids: [a, b],
    },
    {
      id: "osnet-2",
      name: "OSNET-2, Recording Servers 32-46",
      customer_gateway_ip: "69.24.198.150",
      actuate_gateway: "AWS VGW us-east-1",
      kind: "aws_site_to_site",
      status: { status: "down", tunnels_up: 0, tunnels_total: 2 },
      probe_target: "172.16.32.5",
      probe_port: 7563,
      probe_label: "Recording server 32",
      tunnels: [
        {
          outside_ip: "3.209.14.21",
          state: "DOWN",
          status_message: "IPSEC IS DOWN",
          last_status_change: minutesAgo(60 * 26),
        },
        {
          outside_ip: "52.4.88.102",
          state: "DOWN",
          status_message: "IPSEC IS DOWN",
          last_status_change: minutesAgo(60 * 26),
        },
      ],
      routes: ["172.16.32.0/20"],
      site_ids: [c],
    },
    {
      id: "aeronet-1",
      name: "AERONET-1",
      customer_gateway_ip: "173.243.88.74",
      actuate_gateway: "AWS VGW us-east-1",
      kind: "aws_site_to_site",
      status: { status: "up", tunnels_up: 2, tunnels_total: 2 },
      probe_target: "10.40.1.10",
      probe_port: 80,
      probe_label: "Management server",
      tunnels: [
        {
          outside_ip: "3.209.14.30",
          state: "UP",
          status_message: "",
          last_status_change: minutesAgo(60 * 24 * 9),
        },
        {
          outside_ip: "52.4.88.130",
          state: "UP",
          status_message: "",
          last_status_change: minutesAgo(60 * 24 * 9),
        },
      ],
      routes: ["10.40.0.0/16"],
      site_ids: [d],
    },
    {
      id: "depot-direct",
      name: "Depot NVR",
      customer_gateway_ip: "",
      actuate_gateway: "Direct (public IP / port-forward)",
      kind: "direct",
      status: { status: "up", tunnels_up: null, tunnels_total: null },
      probe_target: "203.0.113.40",
      probe_port: 554,
      probe_label: "NVR RTSP",
      tunnels: [],
      routes: [],
      // Third site, so the row survives a scope of three sites (d defaults
      // to a site id the scope does not have).
      site_ids: [c],
    },
  ];
  // The mockup and "site-to-site down" keep the OSNET links down; every other
  // scenario has them up so they don't compete with its own finding.
  return scenario === "mixed" || scenario === "s2s-down"
    ? all
    : all.map(withAwsTunnelsUp);
};

export const SAMPLE_S2S_CHECK = (
  connection: SiteToSiteConnection
): SiteToSiteCheckResult => {
  // A direct path has no tunnels: `every` on [] is true, which read as
  // "tunnel down" (QA CN-24). It has no tunnel or route to check either;
  // only its probe target's reachability says anything.
  const direct = connection.kind === "direct";
  const down =
    connection.tunnels.length > 0 &&
    connection.tunnels.every((t) => t.state !== "UP");
  const directDown = direct && connection.status.status === "down";
  const target = connection.probe_target
    ? `${connection.probe_target}${
        connection.probe_port ? `:${connection.probe_port}` : ""
      }`
    : "the server";
  return {
    rungs: [
      direct
        ? {
            id: "tunnel_status",
            title: "Tunnel status",
            status: "skipped",
            summary:
              "Skipped: this server is reached directly, not over a tunnel.",
          }
        : {
            id: "tunnel_status",
            title: "Tunnel status",
            status: down ? "fail" : "pass",
            summary: down
              ? `Tunnel 1 and tunnel 2: IPSEC IS DOWN. AWS gateway is up; peer ${connection.customer_gateway_ip} is not answering IKE.`
              : "Tunnel 1 UP, tunnel 2 UP.",
          },
      direct
        ? {
            id: "route_present",
            title: "Route to target",
            status: "skipped",
            summary: "Skipped: a direct server needs no VPN route.",
          }
        : {
            id: "route_present",
            title: "Route to target",
            status: "pass",
            summary: "Your server addresses are inside the routes AWS accepts.",
          },
      direct
        ? {
            id: "tunnel_reachability",
            title: "Target reachability (TCP)",
            status: directDown ? "fail" : "pass",
            summary: directDown
              ? `${target} is not answering from Actuate.`
              : `${target} answers from Actuate.`,
          }
        : {
            id: "tunnel_reachability",
            title: "Target reachability (TCP)",
            status: down ? "skipped" : "pass",
            summary: down
              ? "Skipped: nothing can be probed while the tunnel is down."
              : "Management server answers on 80, recording server on 7563.",
          },
      {
        id: "vms_connectivity",
        title: "VMS connectivity (as seen by connectors)",
        status: down || directDown ? "skipped" : "pass",
        summary: directDown
          ? "Skipped: nothing can log in while the server is not answering."
          : down
          ? "Skipped. Last successful Milestone login before the tunnel dropped."
          : "Milestone login succeeded 2 min ago.",
      },
    ],
    classification: directDown
      ? {
          code: "service_unreachable",
          label: "Server Unreachable",
          owner: "customer",
          confidence: "high",
          summary: `${target} is not answering; Actuate's side is up.`,
        }
      : down
      ? {
          code: "tunnel_down",
          label: "Tunnel Down",
          owner: "customer",
          confidence: "high",
          summary: `Both tunnels to ${connection.customer_gateway_ip} are down; AWS is up.`,
        }
      : {
          code: "healthy",
          label: "Healthy",
          owner: "unknown",
          confidence: "high",
          summary: "Every decisive check passed.",
        },
    checked_at: new Date().toISOString(),
  };
};

export const SAMPLE_TELEMETRY = (
  tunnels: WireGuardTunnel[]
): Record<number, WireGuardPeerTelemetry> => {
  const out: Record<number, WireGuardPeerTelemetry> = {};
  tunnels.forEach((tunnel, index) => {
    const stale =
      !tunnel.last_handshake ||
      Date.now() - new Date(tunnel.last_handshake).getTime() > 15 * 60 * 1000;
    out[tunnel.id] = {
      tunnel_id: tunnel.id,
      endpoint: `184.69.112.${8 + (index % 3)}:51820`,
      persistent_keepalive_s: 25,
      rx_bytes_10m: stale ? 0 : 1_200_000 + index * 40_000,
      tx_bytes_10m: stale ? 14_000 : 96_000,
      initiations_without_handshake: stale,
    };
  });
  return out;
};

/**
 * Real tunnels with sample handshake times: all stale for "WireGuard peer
 * stale", all fresh for the other named scenarios, untouched for `mixed`.
 */
export const SAMPLE_TUNNELS = (
  tunnels: WireGuardTunnel[],
  scenario: SampleScenarioKey = "mixed"
): WireGuardTunnel[] => {
  if (scenario === "mixed") return tunnels;
  const handshake = minutesAgo(scenario === "wg-stale" ? 41 : 1);
  return tunnels.map((tunnel) => ({ ...tunnel, last_handshake: handshake }));
};

const listeningReceiver = (receiver: AlarmReceiver): AlarmReceiver => ({
  ...receiver,
  health: {
    listening: "listening",
    detail: "Listening",
    last_accepted_at: minutesAgo(4),
    failures_last_hour: 0,
  },
});

export const SAMPLE_RECEIVERS = (
  siteIds: number[],
  scenario: SampleScenarioKey = "mixed"
): AlarmReceiver[] => {
  const all = MOCKUP_RECEIVERS(siteIds);
  if (scenario === "mixed") return all;
  return all.map((receiver) =>
    scenario === "receiver-refusing" && receiver.type === "immix"
      ? {
          ...receiver,
          health: {
            listening: "refused" as const,
            detail: "Connection refused",
            last_accepted_at: minutesAgo(60 * 3),
            failures_last_hour: 412,
          },
        }
      : listeningReceiver(receiver)
  );
};

const MOCKUP_RECEIVERS = (siteIds: number[]): AlarmReceiver[] => [
  {
    id: "immix-190.63.136.29-25",
    type: "immix",
    label: "Immix receiver (SMTP)",
    host: "190.63.136.29",
    port: 25,
    via_connection: null,
    site_ids: siteIds.slice(0, Math.max(1, Math.ceil(siteIds.length / 2))),
    health: {
      listening: "timed_out",
      detail: "Timed out (8 s)",
      last_accepted_at: minutesAgo(60 * 3),
      failures_last_hour: 2318,
    },
  },
  {
    id: "mes-172.16.254.10-9090",
    type: "milestone_event_server",
    label: "Milestone event server",
    host: "172.16.254.10",
    port: 9090,
    via_connection: { kind: "site-to-site", id: "osnet-1", name: "OSNET-1" },
    site_ids: siteIds.slice(Math.ceil(siteIds.length / 2)),
    health: {
      listening: "listening",
      detail: "Listening",
      last_accepted_at: minutesAgo(4),
      failures_last_hour: 0,
    },
  },
];

const MOCKUP_RECEIVER_TESTS: ReceiverTestRecord[] = [
  {
    id: "t1",
    receiver_id: "immix-190.63.136.29-25",
    sent_at: minutesAgo(50),
    result: "failed",
    detail: "Connection timed out after 8 s",
    sent_by: "you",
  },
  {
    id: "t2",
    receiver_id: "immix-190.63.136.29-25",
    sent_at: minutesAgo(60 * 17),
    result: "delivered",
    detail: "Accepted in 410 ms, SMTP 250",
    sent_by: "you",
  },
  {
    id: "t3",
    receiver_id: "mes-172.16.254.10-9090",
    sent_at: minutesAgo(60 * 49),
    result: "delivered",
    detail: "Accepted in 88 ms",
    sent_by: "Actuate support",
  },
];

export const SAMPLE_RECEIVER_TESTS = (
  scenario: SampleScenarioKey = "mixed"
): ReceiverTestRecord[] => {
  if (scenario === "mixed") return MOCKUP_RECEIVER_TESTS;
  if (scenario === "receiver-refusing")
    return MOCKUP_RECEIVER_TESTS.map((test) =>
      test.id === "t1" ? { ...test, detail: "Connection refused" } : test
    );
  return MOCKUP_RECEIVER_TESTS.map((test) =>
    test.result === "failed"
      ? {
          ...test,
          result: "delivered" as const,
          detail: "Accepted in 380 ms, SMTP 250",
        }
      : test
  );
};

const MOCKUP_SMTP_FILENAMES: SmtpFilenamesResponse = {
  samples: [
    {
      filename: "ch01_20260910_081233.jpg",
      count_12h: 61,
      last_seen: minutesAgo(3),
    },
    {
      filename: "ch02_20260910_081201.jpg",
      count_12h: 54,
      last_seen: minutesAgo(4),
    },
    {
      filename: "ch03_20260910_080947.jpg",
      count_12h: 49,
      last_seen: minutesAgo(6),
    },
    {
      filename: "ch04_20260910_075512.jpg",
      count_12h: 48,
      last_seen: minutesAgo(20),
    },
  ],
  read_filename: true,
  read_filename_expression: "(\\w+)",
  suggested_expression: "(ch\\d+)_",
};

export const SAMPLE_SMTP_FILENAMES = (
  scenario: SampleScenarioKey = "mixed"
): SmtpFilenamesResponse => {
  if (scenario === "mixed") return MOCKUP_SMTP_FILENAMES;
  // "SMTP filename rule mismatch": a rule that extracts nothing from these
  // names. Every other scenario: the rule that does.
  return {
    ...MOCKUP_SMTP_FILENAMES,
    read_filename_expression:
      scenario === "smtp-mismatch" ? "^NVR(\\d+)_" : "(ch\\d+)_",
  };
};

export const SAMPLE_SMTP_RECENT_RECEIVED = (
  sender: string
): SmtpRecentResponse => ({
  emails: [
    {
      received_at: new Date().toISOString(),
      from_address: sender,
      sending_ip: "184.69.112.8",
      tls: false,
      subject: "Actuate",
      attachment_filename: "ch01_20260911_090412.jpg",
      attachment_bytes: 218 * 1024,
      camera_identified: "ch01",
      analysed: true,
    },
  ],
  receiver_accepting: true,
  receiver_emails_10m: 1204,
  last_email_from_site: new Date().toISOString(),
});

export const SAMPLE_SMTP_RECENT_MISSING: SmtpRecentResponse = {
  emails: [],
  receiver_accepting: true,
  receiver_emails_10m: 1204,
  last_email_from_site: minutesAgo(60 * 24 * 3),
};

const KNOWN_PORTS: Record<number, "open" | "closed" | "filtered"> = {
  554: "open",
  80: "open",
  443: "closed",
  7563: "open",
  8000: "filtered",
  37777: "filtered",
  9000: "filtered",
  25: "open",
  51820: "filtered",
};

/** Scenarios where the site's addresses do not answer at all. */
const UNREACHABLE_SCENARIOS: SampleScenarioKey[] = [
  "no-video",
  "wg-stale",
  "s2s-down",
];

export const SAMPLE_PORT_CHECK = (
  request: PortCheckRequest,
  fromLabel: string,
  scenario: SampleScenarioKey = "mixed"
): PortCheckResponse => {
  const results = request.ports.map((port) => {
    const result: "open" | "closed" | "filtered" =
      scenario === "mixed"
        ? KNOWN_PORTS[port] || (port % 3 === 0 ? "filtered" : "open")
        : UNREACHABLE_SCENARIOS.includes(scenario)
        ? "filtered"
        : "open";
    const ms =
      result === "open" ? 11 + (port % 17) : result === "closed" ? 9 : null;
    return { port, result, ms };
  });
  return {
    host: request.host,
    from_label: fromLabel,
    ping: { reply: results.some((r) => r.result !== "filtered"), ms: 12 },
    results,
    checked_at: new Date().toISOString(),
  };
};

export const SAMPLE_CREDENTIAL_CHECK = (
  password: string,
  scenario: SampleScenarioKey = "mixed"
): CredentialCheckResponse => {
  const bad =
    scenario === "milestone-login-refused" ||
    !password ||
    /wrong|bad/i.test(password);
  return {
    tcp: { ok: true, ms: 14 },
    onvif: bad
      ? {
          ok: false,
          http_status: 401,
          detail: 'HTTP 401 Unauthorized, realm "IP Camera"',
        }
      : {
          ok: true,
          http_status: 200,
          detail: "HTTP 200. GetDeviceInformation succeeded",
        },
    rtsp: bad
      ? {
          ok: false,
          rtsp_status: 401,
          detail: "RTSP 401 Unauthorized (digest)",
        }
      : {
          ok: true,
          rtsp_status: 200,
          detail: "RTSP 200 OK, 1 video track H.264 1920x1080",
        },
    device: bad
      ? undefined
      : {
          manufacturer: "Hikvision",
          model: "DS-2CD2143G2-I",
          firmware: "V5.7.3",
        },
    from_label: "Default path",
    attempts_remaining: 4,
    resolved_ip: null,
    checked_at: new Date().toISOString(),
  };
};

export const SAMPLE_CAMERA_LAST_ALARMS = (
  siteId: number,
  scenario: SampleScenarioKey = "mixed"
): CameraLastAlarms => {
  const mockup = MOCKUP_CAMERA_LAST_ALARMS(siteId);
  if (scenario === "mixed") return mockup;
  if (isSilent(scenario))
    return {
      ...mockup,
      rows: mockup.rows.map((row) => ({ ...row, lastAt: null, count: 0 })),
      quiet: mockup.rows.length,
    };
  return {
    ...mockup,
    rows: mockup.rows.map((row, index) => ({
      ...row,
      lastAt: minutesAgo(3 + index * 7),
      count: Math.max(row.count, 5),
    })),
    quiet: 0,
  };
};

const MOCKUP_CAMERA_LAST_ALARMS = (siteId: number): CameraLastAlarms => ({
  siteId,
  windowHours: 48,
  since: minutesAgo(48 * 60),
  rows: [
    { cameraPk: 9007, cameraName: "C7 Loading dock", lastAt: null, count: 0 },
    { cameraPk: 9002, cameraName: "C2 Rear gate", lastAt: null, count: 0 },
    {
      cameraPk: 9005,
      cameraName: "C5 Yard west",
      lastAt: minutesAgo(60 * 31),
      count: 3,
    },
    {
      cameraPk: 9003,
      cameraName: "C3 Car park",
      lastAt: minutesAgo(60 * 6),
      count: 12,
    },
    {
      cameraPk: 9001,
      cameraName: "C1 Entrance",
      lastAt: minutesAgo(41),
      count: 27,
    },
    { cameraPk: 9004, cameraName: "C4", lastAt: minutesAgo(4), count: 58 },
  ],
  quiet: 2,
  truncated: false,
});

/** The SUP-458 story: three port edits and redeploys the same afternoon. */
export const SAMPLE_SITE_CHANGES = (siteId: number): SiteChanges => ({
  siteId,
  windowHours: 24,
  since: minutesAgo(24 * 60),
  until: new Date().toISOString(),
  settingsAvailable: true,
  settingsVersions: [
    {
      versionId: "3q9Vt.sample.c",
      savedAt: minutesAgo(58),
      isLatest: true,
      readable: true,
      savedBy: "support.rivera",
      previousVersionId: "3q9Vt.sample.b",
      changes: [{ path: "server_port", from: 8201, to: 8101 }],
    },
    {
      versionId: "3q9Vt.sample.b",
      savedAt: minutesAgo(60 * 3 + 12),
      isLatest: false,
      readable: true,
      savedBy: "support.rivera",
      previousVersionId: "3q9Vt.sample.a",
      changes: [
        { path: "server_port", from: 8101, to: 8201 },
        {
          path: "cameras[id=1042].password",
          from: "[redacted]",
          to: "[redacted]",
        },
      ],
    },
    {
      versionId: "3q9Vt.sample.a",
      savedAt: minutesAgo(60 * 5 + 40),
      isLatest: false,
      readable: true,
      savedBy: null,
      previousVersionId: null,
      changes: [{ path: "server_port", from: 8101, to: 8201 }],
    },
  ],
  adminChanges: [
    {
      at: minutesAgo(58),
      user: "support.rivera",
      field: "server_port",
      from: 8201,
      to: 8101,
      type: "Changed",
    },
    {
      at: minutesAgo(57),
      user: "support.rivera",
      field: "deployed_date",
      from: minutesAgo(60 * 3 + 11),
      to: minutesAgo(57),
      type: "Changed",
    },
  ],
  connector: {
    podBootTime: minutesAgo(56),
    settingsVersionId: "3q9Vt.sample.c",
    latestSettingsVersionId: "3q9Vt.sample.c",
    latestSettingsSavedAt: minutesAgo(58),
    runningLatest: true,
    deployedDate: minutesAgo(57),
  },
  truncated: false,
  // Last alert a day and a bit ago, just before the default 24 h window.
  markers: {
    lastAlertAt: minutesAgo(60 * 26),
    lastConnectedAt: minutesAgo(60 * 4),
    lastConnectedSource: "camera_status",
  },
});

/**
 * Compare to when it last worked (ENG-2244): the port went back to what it
 * was, so the site's settings match; a flex schedule has no older version.
 */
export const SAMPLE_SETTINGS_COMPARE = (
  siteId: number,
  at: string
): SettingsCompare => ({
  siteId,
  at,
  sources: [
    {
      flexScheduleId: null,
      scheduleName: null,
      settingsKey: `sample/${siteId}/settings.json`,
      baseline: { versionId: "3q9Vt.sample.0", savedAt: minutesAgo(60 * 50) },
      current: { versionId: "3q9Vt.sample.c", savedAt: minutesAgo(58) },
      changes: [],
      identical: true,
      reason: null,
    },
    {
      flexScheduleId: 77,
      scheduleName: "Weekend",
      settingsKey: `sample/${siteId}/flex_77/settings.json`,
      baseline: null,
      current: { versionId: "8Hk2.sample.a", savedAt: minutesAgo(60 * 3) },
      changes: [],
      identical: false,
      reason:
        "No settings version existed at that time, so there is nothing to compare against.",
    },
  ],
});

export const SAMPLE_ALERTS_RECEIVED = (
  siteName: string | null,
  scenario: SampleScenarioKey = "mixed"
): AlertsReceived => {
  if (isSilent(scenario))
    return {
      windowHours: 1,
      since: minutesAgo(60),
      count: 0,
      truncated: false,
      latest: null,
      camerasAlerted: 0,
      latestByCamera: {},
    };
  const latest = minutesAgo(4);
  return {
    windowHours: 1,
    since: minutesAgo(60),
    count: 142,
    truncated: false,
    latest: {
      at: latest,
      siteName: siteName || "GSS-NVR-23218",
      cameraName: "C4",
      sitePk: null,
      cameraPk: 9001,
    },
    camerasAlerted: 37,
    latestByCamera: { 9001: latest },
  };
};

/**
 * The one site's detail with the scenario's arm state (named scenarios only).
 * Everything else, addresses and integration included, stays the real site's.
 */
export const SAMPLE_SITE_DETAIL = (
  detail: SiteConnectionDetail,
  scenario: SampleScenarioKey = "mixed"
): SiteConnectionDetail => {
  switch (scenario) {
    case "mixed":
      return detail;
    case "disarmed":
      return {
        ...detail,
        active: true,
        armed: false,
        armed_status: "NOT_RUNNING",
      };
    case "inactive":
      return {
        ...detail,
        active: false,
        armed: false,
        armed_status: "NOT_RUNNING",
      };
    case "no-video":
    case "wg-stale":
    case "s2s-down":
    case "milestone-login-refused":
      // Armed, no motion: what the site page shows once video stops.
      return { ...detail, active: true, armed: true, armed_status: "WARNING" };
    default:
      return { ...detail, active: true, armed: true, armed_status: "OK" };
  }
};

const LIVENESS_CAMERAS = ["C1 Entrance", "C2 Rear gate", "C3 Car park", "C4"];

/** How long ago each named scenario's cameras last delivered video. */
const videoMinutesAgo = (scenario: SampleScenarioKey): number => {
  switch (scenario) {
    case "connector-silent":
    case "wg-stale":
      return 45;
    case "no-video":
    case "s2s-down":
    case "milestone-login-refused":
      return 90;
    case "disarmed":
    case "inactive":
      return 60 * 20;
    default:
      return 1;
  }
};

/**
 * Per-camera signals for the named scenarios. `mixed` returns null: the page
 * reads the real camera_liveness endpoint, as it did before scenarios.
 */
export const SAMPLE_CAMERA_LIVENESS = (
  siteId: number,
  scenario: SampleScenarioKey = "mixed"
): CameraLiveness | null => {
  if (scenario === "mixed") return null;
  const offline = scenario === "disarmed" || scenario === "inactive";
  const video = videoMinutesAgo(scenario);
  return {
    siteId,
    checkedAt: new Date().toISOString(),
    signals: { lastFrame: true, bandwidth: true, lastImage: false },
    lastFrameSource: "sample",
    connector: {
      lastSeenAt: minutesAgo(
        offline ? 60 * 20 : scenario === "connector-silent" ? 40 : 2
      ),
      censusRunning: !offline,
      inSchedule: true,
    },
    cameras: LIVENESS_CAMERAS.map((cameraName, index) => ({
      cameraId: 9001 + index,
      cameraName,
      host: null,
      continuous: true,
      lastFrameAt: minutesAgo(video + index),
      bandwidthUpdatedAt: minutesAgo(video + index),
      inboundKbps: video > 10 ? 0 : 800,
      lastImageReceivedAt: null,
      brokenFrame: false,
    })),
    errors: [],
  };
};

/**
 * The site's last saved-login result (site_status/) for the named scenarios:
 * refused for "Milestone saved-login refused", fine for "healthy", otherwise
 * null so the real one is read, as before scenarios.
 */
export const SAMPLE_SAVED_LOGIN_STATUS = (
  scenario: SampleScenarioKey = "mixed"
): RefreshStatus | null => {
  const completed = Math.floor(Date.now() / 1000) - 5 * 60;
  if (scenario === "milestone-login-refused")
    return {
      flex_schedule: null,
      last_refresh_started: completed - 20,
      last_refresh_completed: completed,
      connectivity_message: "Unauthorized, check credentials",
      connectivity_reason: "401 Unauthorized from the management server",
    };
  if (scenario === "healthy")
    return {
      flex_schedule: null,
      last_refresh_started: completed - 20,
      last_refresh_completed: completed,
      connectivity_message: "",
      connectivity_reason: "",
    };
  return null;
};
